/**
 * Skills Tab
 * Manages skill configurations for Response Classifier with AI-assisted generation
 */

import React, { useState, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkillEditor } from '@/features/skills/components/SkillEditor';
import { Plus, Zap, Settings2, Info, ChevronDown, ChevronUp, AlertTriangle, AlertCircle, CheckCircle2, Loader2, RotateCcw } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { SkillConfig } from '@/types';
import { useSkillValidation } from '../hooks/useSkillValidation';

// Default skill tool instructions (synced with agents/core/hooks/classifier/hook.py)
const DEFAULT_SKILL_TOOL_INSTRUCTIONS = `你可以使用以下技能工具：

1. get_skill_instructions(skill_id) - 获取技能的完整执行指令
   当你需要执行某个技能但没有自动加载指令时，可以调用此工具获取。
   参数: skill_id (如 "annual_report", "chart_overview")

2. list_available_skills() - 列出所有可用技能
   查看当前agent配置的所有技能及其描述。

使用场景：
- 当 <available_skills> 中列出的技能没有被自动匹配时
- 当你需要查看某个技能的详细指令时
- 当用户明确要求使用某个技能时`;

interface SkillsTabProps {
  skills: SkillConfig[];
  onSkillsChange: (skills: SkillConfig[]) => void;
  // Classifier settings (read-only, controlled from Response Classification view)
  classifierEnabled: boolean;
  classifierVisible?: boolean;
  // Skills settings
  skillsEnabled: boolean;
  onSkillsEnabledChange: (enabled: boolean) => void;
  semanticMatchingEnabled: boolean;
  onSemanticMatchingEnabledChange: (enabled: boolean) => void;
  semanticSimilarityThreshold: number;
  onSemanticSimilarityThresholdChange: (threshold: number) => void;
  // Rule confidence threshold
  ruleConfidenceThreshold: number;
  onRuleConfidenceThresholdChange: (threshold: number) => void;
  // LLM settings for skills
  skillLlmTimeout: number;
  onSkillLlmTimeoutChange: (timeout: number) => void;
  skillToolInstructions: string;
  onSkillToolInstructionsChange: (instructions: string) => void;
  // Context
  agentContext?: string;
}

const DEFAULT_SKILL: Omit<SkillConfig, 'skill_id'> = {
  name: '新技能',
  description: '',
  enabled: true,
  priority: 100,
  exclusion_group: null,
  keywords: [],
  patterns: [],
  example_queries: [],
  instructions: '',
  force_response_type: null,
};

export const SkillsTab: React.FC<SkillsTabProps> = ({
  skills,
  onSkillsChange,
  classifierEnabled,
  classifierVisible = true,
  skillsEnabled,
  onSkillsEnabledChange,
  semanticMatchingEnabled,
  onSemanticMatchingEnabledChange,
  semanticSimilarityThreshold,
  onSemanticSimilarityThresholdChange,
  ruleConfidenceThreshold,
  onRuleConfidenceThresholdChange,
  skillLlmTimeout,
  onSkillLlmTimeoutChange,
  skillToolInstructions,
  onSkillToolInstructionsChange,
  agentContext,
}) => {
  const { messages } = useI18n();
  const t = messages.agentConfig.skills || {
    title: '技能',
    add: '添加技能',
    noSkills: '暂无技能',
    noSkillsHint: '添加技能以启用动态指令注入',
    noSelection: '选择技能',
    noSelectionHint: '从列表中选择一个技能进行编辑，或添加新技能',
    enabled: '已启用',
    disabled: '已禁用',
    priority: '优先级',
    exclusionGroup: '互斥组',
  };

  // Counter for generating unique temporary IDs
  const tempKeyCounter = useRef(0);

  // Skill validation
  const {
    isValid,
    errors,
    warnings,
    isValidating,
    getSkillErrors,
    getSkillWarnings,
  } = useSkillValidation(skills, { debounceMs: 800 });

  // Help section expanded state
  const [helpExpanded, setHelpExpanded] = useState(false);

  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(
    skills.length > 0 ? skills[0].skill_id : null
  );

  const selectedSkill = skills.find((s) => s.skill_id === selectedSkillId);

  // Group skills by exclusion_group
  const groupedSkills = skills.reduce((acc, skill) => {
    const group = skill.exclusion_group || '_ungrouped';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(skill);
    return acc;
  }, {} as Record<string, SkillConfig[]>);

  const handleAddSkill = useCallback(() => {
    const tempId = `skill_${skills.length + 1}_${tempKeyCounter.current++}`;
    const newSkill: SkillConfig = {
      ...DEFAULT_SKILL,
      skill_id: tempId,
      name: `技能 ${skills.length + 1}`,
    };
    onSkillsChange([...skills, newSkill]);
    setSelectedSkillId(newSkill.skill_id);
  }, [skills, onSkillsChange]);

  const handleUpdateSkill = useCallback(
    (skillId: string, updates: Partial<SkillConfig>) => {
      const newSkillId = updates.skill_id;
      onSkillsChange(
        skills.map((s) => (s.skill_id === skillId ? { ...s, ...updates } : s))
      );
      if (newSkillId && newSkillId !== skillId) {
        setSelectedSkillId(newSkillId);
      }
    },
    [skills, onSkillsChange]
  );

  const handleDeleteSkill = useCallback(
    (skillId: string) => {
      const newSkills = skills.filter((s) => s.skill_id !== skillId);
      onSkillsChange(newSkills);
      if (selectedSkillId === skillId) {
        setSelectedSkillId(newSkills.length > 0 ? newSkills[0].skill_id : null);
      }
    },
    [skills, selectedSkillId, onSkillsChange]
  );

  // Check if classifier is visible
  if (!classifierVisible) {
    return (
      <Card className="p-8 text-center">
        <Zap className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">分类器不可用</h3>
        <p className="text-gray-500">
          该 Agent 已隐藏响应分类器 Hook。
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Notice if classifier is not enabled */}
      {!classifierEnabled && (
        <Card className="p-3 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">
              分类器未启用。请先在「响应分类」中启用响应分类器。
            </p>
          </div>
        </Card>
      )}

      {/* Settings Panel - Constrained width for better wide-screen experience */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 className="w-5 h-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">技能匹配设置</h3>
        </div>

        {/* Two-column layout: Switches | Thresholds */}
        <div className="max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Toggle Switches */}
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">功能开关</h4>

            {/* Enable Skills */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                id="skills-enabled"
                checked={skillsEnabled}
                onChange={(e) => onSkillsEnabledChange(e.target.checked)}
                disabled={!classifierEnabled}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-700">启用技能</span>
                <span className="text-xs text-gray-500 ml-2">根据用户意图动态注入指令</span>
              </div>
            </label>

            {/* Enable Semantic Matching */}
            <label className={`flex items-center gap-3 cursor-pointer ${!classifierEnabled || !skillsEnabled ? 'opacity-50' : ''}`}>
              <input
                type="checkbox"
                id="semantic-matching-enabled"
                checked={semanticMatchingEnabled}
                onChange={(e) => onSemanticMatchingEnabledChange(e.target.checked)}
                disabled={!classifierEnabled || !skillsEnabled}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-700">语义匹配</span>
                <span className="text-xs text-gray-500 ml-2">TEI 服务（bge-m3）</span>
              </div>
            </label>
          </div>

          {/* Right: Threshold Sliders */}
          <div className={`space-y-4 p-4 bg-gray-50 rounded-lg ${!classifierEnabled || !skillsEnabled ? 'opacity-50' : ''}`}>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">阈值设置</h4>

            {/* Rule Confidence */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-700">规则置信度</span>
                <span className="text-sm font-mono text-gray-600">{ruleConfidenceThreshold.toFixed(2)}</span>
              </div>
              <input
                type="range"
                id="rule-confidence"
                min="0"
                max="1"
                step="0.05"
                value={ruleConfidenceThreshold}
                onChange={(e) => onRuleConfidenceThresholdChange(parseFloat(e.target.value))}
                disabled={!classifierEnabled || !skillsEnabled}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
            </div>

            {/* Semantic Similarity - only when enabled */}
            {semanticMatchingEnabled && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700">语义相似度</span>
                  <span className="text-sm font-mono text-gray-600">{semanticSimilarityThreshold.toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  id="similarity-threshold"
                  min="0"
                  max="1"
                  step="0.05"
                  value={semanticSimilarityThreshold}
                  onChange={(e) => onSemanticSimilarityThresholdChange(parseFloat(e.target.value))}
                  disabled={!classifierEnabled || !skillsEnabled || !semanticMatchingEnabled}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
              </div>
            )}

            {/* LLM Timeout */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-700">LLM 超时</span>
                <span className="text-sm font-mono text-gray-600">{skillLlmTimeout.toFixed(1)}s</span>
              </div>
              <input
                type="range"
                id="skill-llm-timeout"
                min="1"
                max="30"
                step="0.5"
                value={skillLlmTimeout}
                onChange={(e) => onSkillLlmTimeoutChange(parseFloat(e.target.value))}
                disabled={!classifierEnabled || !skillsEnabled}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
            </div>
          </div>
        </div>

        {/* Skill Tool Instructions - Collapsible */}
        <details className={`mt-4 ${!classifierEnabled || !skillsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <summary className="flex items-center gap-2 cursor-pointer select-none text-sm font-medium text-gray-700 hover:text-gray-900">
            技能工具使用说明
            {skillToolInstructions ? (
              <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">已自定义</span>
            ) : (
              <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">默认</span>
            )}
          </summary>
          <div className="mt-3 max-w-3xl">
            <div className="flex items-center justify-end mb-2">
              {skillToolInstructions && (
                <button
                  type="button"
                  onClick={() => onSkillToolInstructionsChange('')}
                  disabled={!classifierEnabled || !skillsEnabled}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="重置为默认说明"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  重置为默认
                </button>
              )}
            </div>
            <textarea
              id="skill-tool-instructions"
              value={skillToolInstructions || DEFAULT_SKILL_TOOL_INSTRUCTIONS}
              onChange={(e) => {
                const newValue = e.target.value === DEFAULT_SKILL_TOOL_INSTRUCTIONS ? '' : e.target.value;
                onSkillToolInstructionsChange(newValue);
              }}
              disabled={!classifierEnabled || !skillsEnabled}
              className={`w-full h-40 rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm font-mono ${
                !skillToolInstructions ? 'text-gray-500' : ''
              }`}
            />
            <p className="text-xs text-gray-500 mt-1">
              工具 <code className="bg-gray-100 px-1 rounded">get_skill_instructions</code>、<code className="bg-gray-100 px-1 rounded">list_available_skills</code> 的使用说明，注入到系统提示词。
            </p>
          </div>
        </details>
      </Card>

      {/* How It Works - Collapsible Help Section */}
      <Card className="p-4">
        <button
          onClick={() => setHelpExpanded(!helpExpanded)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-500" />
            <h3 className="font-medium text-gray-900">技能匹配原理</h3>
          </div>
          {helpExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {helpExpanded && (
          <div className="mt-4 space-y-4 text-sm text-gray-600">
            {/* Three-Layer Matching */}
            <div>
              <h4 className="font-medium text-gray-800 mb-2">三层级联匹配</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="font-medium text-emerald-800 mb-1">第一层：规则匹配（&lt;10ms）</div>
                  <p className="text-emerald-700 text-xs">
                    匹配 <code className="bg-emerald-100 px-1 rounded">keywords</code>（0.85-0.95 置信度）和{' '}
                    <code className="bg-emerald-100 px-1 rounded">patterns</code>（0.9 置信度）。
                    置信度 ≥ <code className="bg-emerald-100 px-1 rounded">rule_confidence_threshold</code> 时返回结果。
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="font-medium text-blue-800 mb-1">第二层：语义匹配（~50ms）</div>
                  <p className="text-blue-700 text-xs">
                    需启用 <code className="bg-blue-100 px-1 rounded">semantic_matching_enabled</code>。
                    使用 TEI 服务（BAAI/bge-m3）在 Qdrant 中匹配{' '}
                    <code className="bg-blue-100 px-1 rounded">name</code>、
                    <code className="bg-blue-100 px-1 rounded">description</code> 和{' '}
                    <code className="bg-blue-100 px-1 rounded">example_queries</code>。
                    相似度 ≥ <code className="bg-blue-100 px-1 rounded">similarity_threshold</code> 时返回结果。
                  </p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="font-medium text-amber-800 mb-1">第三层：LLM 兜底（~200ms）</div>
                  <p className="text-amber-700 text-xs">
                    使用 <code className="bg-amber-100 px-1 rounded">model_id</code> 进行意图分类。
                    超时：<code className="bg-amber-100 px-1 rounded">skill_llm_timeout</code> 秒。
                    当规则和语义匹配都失败时的兜底方案。
                  </p>
                </div>
              </div>
            </div>

            {/* Exclusion Groups */}
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-800 mb-2">互斥组解析</h4>
              <ul className="text-purple-700 text-xs space-y-1 list-disc list-inside">
                <li>同一 <code className="bg-purple-100 px-1 rounded">exclusion_group</code> 中的技能互斥</li>
                <li>当同组多个技能匹配时，只保留 <code className="bg-purple-100 px-1 rounded">priority</code> 最高的</li>
                <li>不同组的技能可以同时匹配</li>
                <li>没有设置 <code className="bg-purple-100 px-1 rounded">exclusion_group</code> 的技能匹配后始终包含</li>
              </ul>
            </div>

            {/* Field Purposes */}
            <div>
              <h4 className="font-medium text-gray-800 mb-2">技能配置字段说明</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 font-medium text-gray-700">字段</th>
                      <th className="text-left py-2 pr-4 font-medium text-gray-700">用途</th>
                      <th className="text-left py-2 font-medium text-gray-700">提示</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="py-2 pr-4 font-mono text-purple-600">keywords</td>
                      <td className="py-2 pr-4">精确词匹配（第一层）</td>
                      <td className="py-2 text-gray-500">使用特征词："年度"、"报告"、"星盘"</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-purple-600">patterns</td>
                      <td className="py-2 pr-4">正则模式匹配（第一层）</td>
                      <td className="py-2 text-gray-500">用于结构化查询：<code>\d{'{4}'}年.*运势</code></td>
                    </tr>
                    <tr className="bg-blue-50">
                      <td className="py-2 pr-4 font-mono text-blue-600 font-semibold">name / description</td>
                      <td className="py-2 pr-4 font-semibold">语义匹配（第二层）</td>
                      <td className="py-2 text-blue-700">
                        技能名称和描述会被 embedding 以支持语义匹配。
                      </td>
                    </tr>
                    <tr className="bg-blue-50">
                      <td className="py-2 pr-4 font-mono text-blue-600 font-semibold">example_queries</td>
                      <td className="py-2 pr-4 font-semibold">语义匹配（第二层）</td>
                      <td className="py-2 text-blue-700">
                        <strong>最重要！</strong>按用户实际提问方式编写。
                        这些会被 embedding 并存储到 Qdrant。
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-purple-600">instructions</td>
                      <td className="py-2 pr-4">匹配后注入到提示词</td>
                      <td className="py-2 text-gray-500">
                        <strong>不参与匹配</strong>——仅用于执行。
                        编写详细的 Agent 指导说明。
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-purple-600">exclusion_group</td>
                      <td className="py-2 pr-4">互斥控制</td>
                      <td className="py-2 text-gray-500">同组只保留优先级最高的</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Skill Tools */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-800 mb-2">技能工具（注入系统提示词）</h4>
              <p className="text-gray-600 text-xs mb-2">
                启用技能后，Agent 可使用以下工具：
              </p>
              <ul className="text-gray-600 text-xs space-y-1 list-disc list-inside">
                <li><code className="bg-gray-200 px-1 rounded">get_skill_instructions(skill_id)</code> - 获取指定技能的详细指令</li>
                <li><code className="bg-gray-200 px-1 rounded">list_available_skills()</code> - 列出所有可用技能及描述</li>
              </ul>
              <p className="text-gray-500 text-xs mt-2">
                可在上方「技能工具使用说明」中自定义工具使用说明。
              </p>
            </div>

            {/* Example Queries Best Practices */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">编写示例查询的最佳实践</h4>
              <ul className="text-blue-700 text-xs space-y-1 list-disc list-inside">
                <li>每个技能编写 3-10 个不同的示例，覆盖用户的不同提问方式</li>
                <li>包含变体：正式/口语、简短/详细、不同表达方式</li>
                <li>聚焦于<strong>用户意图</strong>，而非技术术语</li>
                <li>好的示例："帮我看看明年运势"、"2025年我的运气怎么样"、"想了解一下未来一年的发展"</li>
                <li>避免：复制 instructions 内容——那是用于执行的，不是匹配的</li>
              </ul>
            </div>

            {/* Semantic Matching Note */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-medium text-amber-800 mb-2">语义匹配详情</h4>
              <ul className="text-amber-700 text-xs space-y-1 list-disc list-inside">
                <li>使用系统配置的 TEI 服务（BAAI/bge-m3）进行 embedding</li>
                <li><code className="bg-amber-100 px-1 rounded">name</code>、<code className="bg-amber-100 px-1 rounded">description</code> 和 <code className="bg-amber-100 px-1 rounded">example_queries</code> 都会被索引到 Qdrant</li>
                <li><code className="bg-amber-100 px-1 rounded">instructions</code> 不参与匹配（它定义执行行为）</li>
                <li>保存配置时会自动重新索引 embeddings</li>
                <li>阈值控制最低相似度分数（默认 0.75 = 75% 相似）</li>
                <li>每个技能可匹配多个文本，只使用最高分数</li>
              </ul>
            </div>
          </div>
        )}
      </Card>

      {/* Validation Status Banner */}
      {skills.length > 0 && (errors.length > 0 || warnings.length > 0) && (
        <Card className={`p-4 ${errors.length > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-start gap-3">
            {errors.length > 0 ? (
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <h4 className={`font-medium ${errors.length > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                {errors.length > 0 ? `发现 ${errors.length} 个验证错误` : `${warnings.length} 个警告`}
              </h4>
              <ul className={`mt-2 text-sm space-y-1 ${errors.length > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                {errors.slice(0, 3).map((err, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="font-mono text-xs bg-white/50 px-1 rounded">{err.skill_id || '全局'}</span>
                    <span>{err.message}</span>
                  </li>
                ))}
                {warnings.slice(0, errors.length > 0 ? 0 : 3).map((warn, i) => (
                  <li key={`w-${i}`} className="flex items-start gap-2">
                    <span className="font-mono text-xs bg-white/50 px-1 rounded">{warn.skill_id || '全局'}</span>
                    <span>{warn.message}</span>
                  </li>
                ))}
                {(errors.length > 3 || (errors.length === 0 && warnings.length > 3)) && (
                  <li className="text-xs opacity-75">
                    ...还有 {errors.length > 3 ? errors.length - 3 : warnings.length - 3} 项
                  </li>
                )}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Skills List and Editor */}
      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[600px] ${!classifierEnabled || !skillsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Left: Skill List */}
        <Card className="p-4 lg:col-span-1">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-500" />
              <h3 className="font-medium text-gray-900">{t.title}</h3>
              <span className="text-sm text-gray-500">({skills.length})</span>
              {/* Validation Status Indicator */}
              {skills.length > 0 && (
                isValidating ? (
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                ) : isValid && warnings.length === 0 ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : !isValid ? (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                )
              )}
            </div>
            <Button size="sm" onClick={handleAddSkill} disabled={!classifierEnabled || !skillsEnabled}>
              <Plus className="w-4 h-4 mr-1" />
              {t.add}
            </Button>
          </div>

          {skills.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t.noSkills}</p>
              <p className="text-xs mt-1">{t.noSkillsHint}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Ungrouped skills first */}
              {groupedSkills['_ungrouped'] && (
                <div className="space-y-2">
                  {groupedSkills['_ungrouped'].map((skill) => (
                    <SkillListItem
                      key={skill.skill_id}
                      skill={skill}
                      isSelected={selectedSkillId === skill.skill_id}
                      onClick={() => setSelectedSkillId(skill.skill_id)}
                      t={t}
                      errorCount={getSkillErrors(skill.skill_id).length}
                      warningCount={getSkillWarnings(skill.skill_id).length}
                    />
                  ))}
                </div>
              )}

              {/* Grouped skills */}
              {Object.entries(groupedSkills)
                .filter(([group]) => group !== '_ungrouped')
                .map(([group, groupSkills]) => (
                  <div key={group}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-px flex-1 bg-gray-200" />
                      <span className="text-xs text-gray-500 font-medium px-2">
                        {t.exclusionGroup}: {group}
                      </span>
                      <div className="h-px flex-1 bg-gray-200" />
                    </div>
                    <div className="space-y-2">
                      {groupSkills.map((skill) => (
                        <SkillListItem
                          key={skill.skill_id}
                          skill={skill}
                          isSelected={selectedSkillId === skill.skill_id}
                          onClick={() => setSelectedSkillId(skill.skill_id)}
                          t={t}
                          errorCount={getSkillErrors(skill.skill_id).length}
                          warningCount={getSkillWarnings(skill.skill_id).length}
                        />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Card>

        {/* Right: Skill Editor */}
        <div className="lg:col-span-2">
          {selectedSkill ? (
            <SkillEditor
              skill={selectedSkill}
              onChange={(updates) => handleUpdateSkill(selectedSkillId!, updates)}
              onDelete={() => handleDeleteSkill(selectedSkillId!)}
              agentContext={agentContext}
            />
          ) : (
            <Card className="h-full flex flex-col items-center justify-center text-gray-400 p-8">
              <Zap className="w-16 h-16 mb-4 opacity-20" />
              <p className="text-lg font-medium">{t.noSelection}</p>
              <p className="text-sm mt-1">{t.noSelectionHint}</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Skill list item component
interface SkillListItemProps {
  skill: SkillConfig;
  isSelected: boolean;
  onClick: () => void;
  t: Record<string, string>;
  errorCount?: number;
  warningCount?: number;
}

const SkillListItem: React.FC<SkillListItemProps> = ({
  skill,
  isSelected,
  onClick,
  t,
  errorCount = 0,
  warningCount = 0,
}) => (
  <div
    className={`p-3 rounded-lg cursor-pointer border transition-colors ${
      isSelected
        ? 'border-purple-500 bg-purple-50'
        : errorCount > 0
        ? 'border-red-300 bg-red-50 hover:bg-red-100'
        : warningCount > 0
        ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
        : 'border-gray-200 hover:bg-gray-50'
    } ${!skill.enabled ? 'opacity-60' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 min-w-0">
        {/* Validation indicator */}
        {errorCount > 0 ? (
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
        ) : warningCount > 0 ? (
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
        ) : null}
        <span className="font-medium text-gray-900 truncate">{skill.name}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {skill.force_response_type && (
          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
            {skill.force_response_type}
          </span>
        )}
        <span
          className={`px-2 py-0.5 text-xs rounded ${
            skill.enabled
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          {skill.enabled ? t.enabled : t.disabled}
        </span>
      </div>
    </div>
    <div className="text-xs text-gray-500 mt-1 font-mono truncate">
      {skill.skill_id}
    </div>
    {skill.description && (
      <p className="text-sm text-gray-500 mt-1 truncate">{skill.description}</p>
    )}
    <div className="flex items-center gap-3 text-xs text-gray-400 mt-2">
      <span>{t.priority}: {skill.priority}</span>
      <span>•</span>
      <span>{skill.keywords.length} keywords</span>
      <span>•</span>
      <span>{skill.example_queries.length} examples</span>
      {/* Validation counts */}
      {(errorCount > 0 || warningCount > 0) && (
        <>
          <span>•</span>
          {errorCount > 0 && (
            <span className="text-red-500">{errorCount} error{errorCount > 1 ? 's' : ''}</span>
          )}
          {warningCount > 0 && errorCount === 0 && (
            <span className="text-amber-500">{warningCount} warning{warningCount > 1 ? 's' : ''}</span>
          )}
        </>
      )}
    </div>
  </div>
);

export default SkillsTab;
